import { useState, useCallback, useRef, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { useInsufficientCredits } from '../contexts/InsufficientCreditsContext';

/**
 * Hook for managing credit warning modals before credit-spending operations
 * 
 * Features:
 * - Shows warning modal with credit cost before operations
 * - Allows users to opt-out per operation type
 * - Caches user preferences for performance
 * - Optimistically updates preferences
 * 
 * @returns {Object} Warning control methods and state
 */
const useCreditWarning = () => {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [warningData, setWarningData] = useState(null);
  const pendingActionRef = useRef(null);
  const pendingRejectRef = useRef(null);
  const toast = useToast();
  const location = useLocation();
  const { showInsufficientCreditsModal } = useInsufficientCredits();

  // Cache user preferences (fetched from users/me)
  const [userPreferences, setUserPreferences] = useState(null);
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState(null);

  // Fetch user preferences and subscription status
  const fetchUserPreferences = useCallback(async () => {
    try {
      const [meRes, subRes] = await Promise.all([
        api.get('/api/auth/users/me'),
        api.get('/api/payments/subscription-status').catch(() => ({ data: null })),
      ]);
      const preferences = meRes.data.preferences || {};
      setUserPreferences(preferences);

      const sub = subRes?.data;
      setUserSubscriptionStatus({
        subscription: meRes.data.subscription || 'free',
        new_subscription: meRes.data.new_subscription,
        ...(sub && typeof sub === 'object' ? sub : {}),
      });

      return preferences;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      return null;
    }
  }, []);

  // Fetch preferences on mount
  useEffect(() => {
    fetchUserPreferences();
  }, [fetchUserPreferences]);

  // Map operation type to preference key
  const getPreferenceKey = useCallback((operationType) => {
    const preferenceKeyMap = {
      contact_enrichment: 'warn_enrich',
      contact_address: 'warn_contact_address',
      email_verification: 'warn_email_verification',
      email_draft: 'warn_email_draft',
      custom_field: 'warn_custom_field',
      run_for_all: 'warn_custom_field', // shares the custom_field opt-out preference
      heyreach_export: 'warn_heyreach_export',
      smartlead_export: 'warn_smartlead_export',
      file_upload: 'warn_file_upload',
    };
    return preferenceKeyMap[operationType];
  }, []);

  // Check if warning should be shown and get credit cost
  const checkCreditWarning = useCallback(async (operationType, params) => {
    try {
      // Fetch preferences if not cached
      let preferences = userPreferences;
      if (!preferences) {
        preferences = await fetchUserPreferences();
      }

      // Check if user opted out of warnings for this operation
      const preferenceKey = getPreferenceKey(operationType);
      const shouldWarn = preferences?.[preferenceKey] !== false;

      // If user opted out, return null (no warning needed)
      if (!shouldWarn) {
        return null;
      }

      // Call preview endpoint to get cost
      const response = await api.post('/api/preview-token-spend', {
        operation_type: operationType,
        ...params,
      });

      // Check if this is a signup-required response (not an error anymore)
      if (response.requiresSignup) {
        // Signup modal already triggered by interceptor
        // Return null to indicate no preview available yet
        return null;
      }
      
      return response.data;
    } catch (error) {
      // Real errors only (403 signup-required is handled above as resolved promise)
      console.error('Error checking credit warning:', error);
      toast({
        title: 'Error',
        description: 'Failed to calculate credit cost',
        status: 'error',
        duration: 3000,
      });
      throw error;
    }
  }, [userPreferences, fetchUserPreferences, getPreferenceKey, toast]);

  // Show warning modal and wait for user confirmation
  const showCreditWarning = useCallback((warningInfo, onConfirm) => {
    setWarningData(warningInfo);
    pendingActionRef.current = onConfirm;
    setIsWarningOpen(true);
  }, []);

  // Handle user confirmation
  const handleConfirm = useCallback(() => {
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
    setIsWarningOpen(false);
    setWarningData(null);
  }, []);

  // Handle user opt-out
  const handleOptOut = useCallback(async (operationType) => {
    try {
      const preferenceKey = getPreferenceKey(operationType);

      // Optimistically update local cache
      setUserPreferences(prev => ({
        ...prev,
        [preferenceKey]: false,
      }));

      // Update backend
      await api.patch('/api/user/preferences', {
        [preferenceKey]: false,
      });
    } catch (error) {
      console.error('Error updating preference:', error);
      // Revert optimistic update
      await fetchUserPreferences();
      toast({
        title: 'Error',
        description: 'Failed to update preference',
        status: 'error',
        duration: 3000,
      });
    }
  }, [getPreferenceKey, fetchUserPreferences, toast]);

  // Handle insufficient credits - redirect to checkout with pending action
  const handleInsufficientCredits = useCallback(async (creditsNeeded, currentBalance, actionDetails) => {
    try {
      console.log('[useCreditWarning] Handling insufficient credits:', { creditsNeeded, currentBalance, actionDetails });
      
      // Store pending action in localStorage for execution after purchase
      localStorage.setItem('pending_action', JSON.stringify(actionDetails));
      
      // Calculate how many credit bundles needed
      const creditsToAdd = Math.max(creditsNeeded - currentBalance, 0);
      const creditsPerBundle = 1000;
      const bundlesNeeded = Math.ceil(creditsToAdd / creditsPerBundle);
      
      console.log('[useCreditWarning] Bundles needed:', bundlesNeeded);
      
      // Create Stripe checkout session
      const currentUrl = window.location.origin + location.pathname;
      const successUrl = `${currentUrl}?stripe_success=true`;
      const cancelUrl = `${currentUrl}?stripe_cancel=true`;
      
      const response = await api.post('/api/payments/create-checkout-session', {
        payment_type: 'credit_purchase',
        product_key: 'credit_bundle_1k',
        quantity: bundlesNeeded,
        success_url: successUrl,
        cancel_url: cancelUrl
      });
      
      console.log('[useCreditWarning] Checkout session created:', response.data);
      
      // Redirect to Stripe hosted checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('[useCreditWarning] Error creating checkout session:', error);
      toast({
        title: 'Error',
        description: 'Failed to initiate checkout. Please try again.',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [location, toast]);

  // Main wrapper function - wraps credit-spending actions with warning
  const withCreditWarning = useCallback(async (operationType, params, action) => {
    try {
      // XLSX campaign export: no preview modal and no server-side credit deduction
      if (operationType === 'export') {
        return await action();
      }

      // Check if warning needed and get cost
      const warningInfo = await checkCreditWarning(operationType, params);

      if (!warningInfo || warningInfo.credits_needed === 0) {
        // No warning needed (user opted out, or this operation costs nothing)
        return await action();
      }

      if (
        warningInfo.preview_block_code === 'overage_disabled' ||
        warningInfo.preview_block_code === 'overage_cap_exceeded'
      ) {
        return await new Promise((resolve, reject) => {
          pendingRejectRef.current = reject;
          showInsufficientCreditsModal({
            message: warningInfo.preview_block_message,
            code: warningInfo.preview_block_code,
            balance: warningInfo.balance,
            credits_needed: warningInfo.credits_needed,
            subscription: warningInfo.subscription,
            credit_overage_spend_cap_cents: warningInfo.credit_overage_spend_cap_cents,
            credit_overage_spend_cents_this_period: warningInfo.credit_overage_spend_cents_this_period,
            overage_price_per_credit_cents: warningInfo.overage_price_per_credit_cents,
            overage_dollars_per_credit: warningInfo.overage_dollars_per_credit,
            subscription_monthly_price_cents: warningInfo.subscription_monthly_price_cents,
            overage_credits_for_request: warningInfo.overage_credits_for_request,
            required_overage_spend_cents: warningInfo.required_overage_spend_cents,
            remaining_overage_budget_cents: warningInfo.remaining_overage_budget_cents,
            onCapSaved: async () => {
              pendingRejectRef.current = null;
              try {
                const result = await action();
                resolve(result);
              } catch (error) {
                reject(error);
              }
            },
            onDismissWithoutResume: () => {
              const r = pendingRejectRef.current;
              pendingRejectRef.current = null;
              if (r) {
                const err = new Error('creditWarningCancelled');
                err.isCancellation = true;
                r(err);
              }
            },
          });
        });
      }

      // Show warning and wait for user confirmation.
      // pendingRejectRef is set so closeWarning() can cancel this promise
      // if the user presses Cancel (otherwise the promise would hang forever).
      // NOTE: `await` here is intentional — it lets the surrounding try/catch
      // intercept the cancellation rejection so callers don't need their own
      // isCancellation guard.
      return await new Promise((resolve, reject) => {
        pendingRejectRef.current = reject;
        showCreditWarning(warningInfo, async () => {
          pendingRejectRef.current = null;
          try {
            const result = await action();
            resolve(result);
          } catch (error) {
            reject(error);
          }
        });
      });
    } catch (error) {
      if (error?.isCancellation) {
        throw error; // let callers reset loading state (finally / catch)
      }
      console.error('[useCreditWarning] Error in withCreditWarning:', error);
      throw error;
    }
  }, [checkCreditWarning, showCreditWarning, showInsufficientCreditsModal]);

  return {
    withCreditWarning,
    isWarningOpen,
    warningData,
    handleConfirm,
    handleOptOut,
    handleInsufficientCredits,
    closeWarning: () => {
      setIsWarningOpen(false);
      setWarningData(null);
      pendingActionRef.current = null;
      // Reject any pending withCreditWarning promise so callers' finally blocks run.
      if (pendingRejectRef.current) {
        const reject = pendingRejectRef.current;
        pendingRejectRef.current = null;
        const err = new Error('creditWarningCancelled');
        err.isCancellation = true;
        reject(err);
        window.dispatchEvent(new CustomEvent('creditWarningCancelled'));
      }
   },
    fetchUserPreferences,
    userSubscriptionStatus,
  };
};

export default useCreditWarning;

